import React, {useRef, useState, useMemo} from 'react';
import {DateField, NumberField, TextField, Title, useQueryWithStore} from "react-admin";
import {Card, CardContent, Box, Paper, Divider} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import ReactToPrint from "react-to-print";
import Button from "@material-ui/core/Button";
import PrintIcon from "@material-ui/icons/Print";
import SearchIcon from "@material-ui/icons/Search";
import {MonthSelection} from "../employeeReport";
import {connect, useDispatch} from "react-redux";
import Typography from "@material-ui/core/Typography";
import MyDatagrid from "../../accountsGrid";
import MultiCurrencyField from "../../../UIComponents/multiCurrencyField";
import MultiSelect from "react-multi-select-component";
import PayrollStatementExcelExport from "./payrollStatementExcelExport";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import GroupIcon from "@material-ui/icons/GroupWork";
import get from "lodash/get";
import {makeStyles} from "@material-ui/core/styles";

const summeryFields = ['basicSalary', 'leaveDays', 'absentDays','totalIncome','taxableIncome', 'incomeTax', 'empPension', 'companyPension', 'cash'];

const useStyles = makeStyles((theme) => ({
    filterBar: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.type === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'visible',
    },
    filterItem: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    actionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    searchButton: {
        minWidth: 120,
        height: 40,
        boxShadow: 'none',
    },
    sectionLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: theme.spacing(0.5),
    },
    groupSwitch: {
        marginLeft: 0,
        marginRight: theme.spacing(1),
        backgroundColor: theme.palette.action.hover,
        borderRadius: 20,
        paddingRight: theme.spacing(1),
    },
    employeeMultiSelect: {
        position: 'relative',
        '& .rmsc': {
            minWidth: 260,
        },
        '& .rmsc .dropdown-container': {
            position: 'relative',
            zIndex: 1300,
        },
        '& .rmsc .dropdown-content': {
            zIndex: 1301,
            boxShadow: theme.shadows[8],
        },
        '& .rmsc .dropdown-content .panel-content': {
            maxHeight: 320,
            overflow: 'visible',
        },
        '& .rmsc .dropdown-content .options': {
            maxHeight: 260,
            overflowY: 'auto',
        },
    },
    exportRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        marginTop: theme.spacing(1),
    },
    toolbarDivider: {
        margin: theme.spacing(1, 0),
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
    contentCard: {
        overflow: 'visible',
    },
    contentCardContent: {
        overflow: 'visible',
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
}));

const EmployeePayrollStatement = ({payrolls}) => {
    const classes = useStyles();
    const componentRef = useRef();
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedRuns, setSelectedRuns] = useState([]);
    const [groupingEnabled, setGroupingEnabled] = useState(true);

    const dispatch = useDispatch();
    const handleOk = (payrollRuns)=>{
        dispatch({
            type: "GET_PAYROLL_STATEMENT",
            payload: {
                payrollRuns,
                employeeIds: selectedEmployees.map(e => e.value),
            }
        })
    }
     const {data: employees =[] } = useQueryWithStore({
         type: 'getList',
         resource: 'employees',
     });

    const filteredPayrolls = payrolls.filter(p=>!selectedEmployees.length||selectedEmployees.map(e=>e.value).includes(p.employeeId));

    const groupedPayrolls = useMemo(() => {
        if (!groupingEnabled) return null;
        const groups = {};
        filteredPayrolls.forEach((p, idx) => {
            const key = p.employeeId || p.fullName;
            if (!groups[key]) {
                groups[key] = { employee: p.fullName, items: [] };
            }
            groups[key].items.push({ ...p, _originalIndex: idx });
        });
        return Object.values(groups);
    }, [filteredPayrolls, groupingEnabled]);

    const excelData = useMemo(() => {
        if (!groupingEnabled) return filteredPayrolls;
        const rows = [];
        groupedPayrolls.forEach(group => {
            group.items.forEach(item => {
                rows.push(item);
            });
            rows.push({
                _type: 'subtotal',
                fullName: `${group.employee} - Total`,
                basicSalary: group.items.reduce((s, i) => s + get(i, 'basicSalary', 0), 0),
                leaveDays: group.items.reduce((s, i) => s + get(i, 'leaveDays', 0), 0),
                absentDays: group.items.reduce((s, i) => s + get(i, 'absentDays', 0), 0),
                totalIncome: group.items.reduce((s, i) => s + get(i, 'totalIncome', 0), 0),
                taxableIncome: group.items.reduce((s, i) => s + get(i, 'taxableIncome', 0), 0),
                incomeTax: group.items.reduce((s, i) => s + get(i, 'incomeTax', 0), 0),
                empPension: group.items.reduce((s, i) => s + get(i, 'empPension', 0), 0),
                companyPension: group.items.reduce((s, i) => s + get(i, 'companyPension', 0), 0),
                cash: group.items.reduce((s, i) => s + get(i, 'cash', 0), 0),
            });
        });
        return rows;
    }, [filteredPayrolls, groupedPayrolls, groupingEnabled]);

    const initColumns = [
        {field:"sn", width:80, show:true, title:"SN", type:"Text"},
        {show:true,field:"fullName", width:230, title:"Full Name", type:"Text"},
        {show:true,field:`payrollRun.payrollDate`,  title:"Payroll Date", type:"Date"},
        {show:true,field:`employee.startDate`,  title:"Start Date", type:"Date"},
        {show:true,field:`basicSalary`,  title:"Basic Salary",  type:"Number"},
        {show:true,field:`leaveDays`,  title:"Leave Days",  type:"Number"},
        {show:true,field:`absentDays`,  title:"Absent Days",  type:"Number"},
        {show:true,field:`totalIncome`,  title:"Total Income",  type:"Number"},
        {show:true,field:`taxableIncome`,  title:"Taxable Income",  type:"Number"},
        {show:true,field:`incomeTax`,  title:"Income Tax",  type:"Number"},
        {show:true,field:`empPension`,  title:"Pension Employee",  type:"Number"},
        {show:true,field:`companyPension`,  title:"Pension Company",  type:"Number"},
        {show:true,field:`cash`,  title:"Net Pay",  type:"Number"},
    ];

    const renderFlatDatagrid = () => (
        <MyDatagrid fullHeight checkField={false} summeryFields={summeryFields} data={filteredPayrolls} ids={filteredPayrolls.map((e,i)=>i)} currentSort={{ field: 'payrollDate', order: 'desc' }} setSort={()=>{}}>
            <TextField source="fullName" />
            <DateField label="Payroll Date" source="payrollRun.payrollDate"/>
            <DateField label="Start Date" source="employee.startDate"/>
            <MultiCurrencyField label={"Salary"} source={"basicSalary"}/>
            <NumberField label={"Leave Days"} source={"leaveDays"}/>
            <NumberField label={"Absent Days"} source={"absentDays"}/>
            <MultiCurrencyField label={"Gross Income"} source={"totalIncome"}/>
            <MultiCurrencyField label={"Taxable Income"} source={"taxableIncome"}/>
            <MultiCurrencyField label={"Income Tax"} source={"incomeTax"}/>
            <MultiCurrencyField label={"Pension Employee"} source={"empPension"}/>
            <MultiCurrencyField label={"Pension Company"} source={"companyPension"}/>
            <MultiCurrencyField label={"Net Pay"} source={"cash"}/>
        </MyDatagrid>
    );

    const renderGroupedTable = () => (
        <div ref={componentRef}>
            {groupedPayrolls.map((group, gIdx) => (
                <div key={gIdx} style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', padding: '8px 0' }}>
                        {group.employee}
                    </Typography>
                    <MyDatagrid fullHeight checkField={false} summeryFields={summeryFields} data={group.items} ids={group.items.map((_, i) => i)} currentSort={{ field: 'payrollDate', order: 'desc' }} setSort={()=>{}}>
                        <TextField source="fullName" />
                        <DateField label="Payroll Date" source="payrollRun.payrollDate"/>
                        <DateField label="Start Date" source="employee.startDate"/>
                        <MultiCurrencyField label={"Salary"} source={"basicSalary"}/>
                        <NumberField label={"Leave Days"} source={"leaveDays"}/>
                        <NumberField label={"Absent Days"} source={"absentDays"}/>
                        <MultiCurrencyField label={"Gross Income"} source={"totalIncome"}/>
                        <MultiCurrencyField label={"Taxable Income"} source={"taxableIncome"}/>
                        <MultiCurrencyField label={"Income Tax"} source={"incomeTax"}/>
                        <MultiCurrencyField label={"Pension Employee"} source={"empPension"}/>
                        <MultiCurrencyField label={"Pension Company"} source={"companyPension"}/>
                        <MultiCurrencyField label={"Net Pay"} source={"cash"}/>
                    </MyDatagrid>
                </div>
            ))}
        </div>
    );

    return (
        <Card className={classes.contentCard}>
            <Title title="Employee Payroll Statement"/>
            <Card className={classes.contentCard}>
                <CardContent className={classes.contentCardContent}>
                    <div className={classes.headerTitle}>
                        <Typography variant="h6" style={{fontWeight: 600}}>
                            Payroll Statement
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Select months and employees, then search to generate
                        </Typography>
                    </div>

                    <Paper elevation={0} className={classes.filterBar}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} sm={5} md={4}>
                                <div className={classes.filterItem}>
                                    <span className={classes.sectionLabel}>Payroll Months</span>
                                    <MonthSelection
                                        selected={selectedRuns}
                                        onSelectedChange={setSelectedRuns}
                                        hideButton
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={5} md={4}>
                                <div className={classes.filterItem}>
                                    <span className={classes.sectionLabel}>Employees</span>
                                    <div className={classes.employeeMultiSelect}>
                                        <MultiSelect
                                            value={selectedEmployees}
                                            options={employees.map(e=>({label:(e.orgID||'')+(e.orgID?'-':'')+e.fullname,value:e.id}))}
                                            onChange={values=>{ setSelectedEmployees(values) }}
                                            labelledBy="Select Employees"
                                            valueRenderer={(selected, _options) => {
                                                if(!selected.length) return "Filter By Employees";
                                                if(selected.length === employees.length) return ["All Employees"];
                                                if(selected.length>3) return selected.slice(0, 3).map(({ label }) => `${label}, `).concat(` + ${selected.length-3}`);
                                                return selected.map(({ label }) => `${label}, `);
                                            }}
                                        />
                                    </div>
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={2} md={4}>
                                <div className={classes.actionRow} style={{justifyContent: 'flex-end'}}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.searchButton}
                                        startIcon={<SearchIcon/>}
                                        onClick={() => handleOk(selectedRuns)}
                                        disabled={!selectedRuns.length}
                                    >
                                        Search
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Divider className={classes.toolbarDivider}/>

                    <div className={classes.exportRow}>
                        <ReactToPrint
                            trigger={() => <Button variant="outlined" color="primary" startIcon={<PrintIcon/>}>Print/PDF</Button>}
                            content={() => componentRef.current}
                            pageStyle={""}
                        />
                        <PayrollStatementExcelExport label="Export Statement" footerItems={[]} showFooter={false} data={excelData} columns={initColumns} fileName={'PayrollStatementReport.xlsx'} title={`Payroll Statement Report`} groupingEnabled={groupingEnabled}/>
                        <Box flexGrow={1}/>
                        <FormControlLabel
                            className={classes.groupSwitch}
                            control={<Switch checked={groupingEnabled} onChange={(e) => setGroupingEnabled(e.target.checked)} color="primary"/>}
                            label={<span style={{display: 'inline-flex', alignItems: 'center', fontSize: '0.85rem'}}><GroupIcon style={{fontSize: 18, marginRight: 4}}/>Group by Employee</span>}
                            labelPlacement="start"
                        />
                    </div>

                    <Grid ref={!groupingEnabled ? componentRef : null} direction="column" container spacing={10}>
                        <Grid item>
                            {groupingEnabled ? renderGroupedTable() : renderFlatDatagrid()}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Card>
    );
};

export default connect(state=>({
   payrolls: state.report.payrollStatement,
}))(EmployeePayrollStatement);
